import { useEffect, useState } from "react";

import { cn } from "@/lib/utils";
import { getTerms, type TermsItem } from "@/services/navigator/signup-service";
import { NvButton } from "./nv-button";
import { NvModal } from "./nv-dialog";
import { NvLucideXIcon } from "./nv-icons-finder";
import { NvTabItem } from "./nv-tab-item";

// 약관 전문 보기 (Figma 203:6760) — 회원가입 · Contact Us 공용. Tabs are the given terms codes.
const TAB_LABELS: Record<string, string> = {
  service_terms: "이용약관",
  privacy_signup: "개인정보 수집 및 이용",
  privacy_policy: "개인정보 처리방침",
  // Contact Us 회원은 처리방침 · 수집·이용 · 제3자 제공이 한 팝업에 함께 나와 탭 이름을 나눠요 (10/12 화면)
  privacy_inquiry: "개인정보 수집·이용",
  supplier_contact_share: "개인정보 제3자 제공",
  marketing: "마케팅 정보 수신",
};

export interface NvTermsModalProps {
  codes: string[];
  // Open tab; null = closed
  openCode: string | null;
  onOpenCodeChange: (code: string | null) => void;
  // 화면이 탭 이름을 따로 정할 때 (회원가입: Figma ver.7 "개인정보 수집 및 이용 동의서" · "개인정보 제3자 제공 동의서"). 없는 코드는 기본 이름
  labels?: Partial<Record<string, string>>;
}

function NvTermsModal({ codes, openCode, onOpenCodeChange, labels }: NvTermsModalProps) {
  const [terms, setTerms] = useState<TermsItem[]>([]);
  const codeKey = codes.join(",");

  useEffect(() => {
    getTerms(codeKey.split(",")).then(setTerms).catch(() => setTerms([]));
  }, [codeKey]);

  const open = terms.find((t) => t.code === openCode) ?? null;

  return (
    <NvModal
      open={openCode !== null}
      onOpenChange={(isOpen) => !isOpen && onOpenCodeChange(null)}
      title="약관 전문 보기"
      closeIcon={<NvLucideXIcon size={24} />}
      footer={<NvButton onClick={() => onOpenCodeChange(null)}>닫기</NvButton>}
    >
      <div role="tablist" className="flex items-center gap-6 border-b border-nv-border">
        {codes.map((code) => (
          <NvTabItem
            key={code}
            active={openCode === code}
            onClick={() => onOpenCodeChange(code)}
            className={cn(
              "px-0 pb-[10px] pt-2 text-[14px] leading-[17px]",
              openCode === code ? "border-nv-text-primary font-medium text-nv-text-primary" : "text-nv-text-secondary",
            )}
          >
            {labels?.[code] ?? TAB_LABELS[code] ?? code}
          </NvTabItem>
        ))}
      </div>
      <div className="h-[380px] overflow-y-auto pr-2">
        {open ? (
          <>
            <h3 className="text-[16px] font-medium leading-[19px] text-nv-text-primary">{open.title}</h3>
            <p className="mt-[10px] whitespace-pre-line text-[13px] leading-[21px] text-nv-text-secondary">{open.body}</p>
            <p className="mt-[10px] text-[12px] leading-[19px] text-nv-text-secondary">시행 버전 {open.version}</p>
          </>
        ) : (
          <p className="text-[13px] leading-[21px] text-nv-text-secondary">약관을 불러오지 못했습니다.</p>
        )}
      </div>
    </NvModal>
  );
}

export { NvTermsModal };
